/**
 * WellRegistrationTotals - Main container component
 * @module wellRegistrationTotals/WellRegistrationTotals
 */

import React, { useRef, useMemo, useCallback } from "react";
import { Typography, CardContent, Box, Button } from "@material-ui/core";
import { Alert, AlertTitle } from "@material-ui/lab";
import { Helmet } from "react-helmet-async";
import Link from "@material-ui/core/Link";
import { NavLink } from "react-router-dom";
import { CSVLink } from "react-csv";
import GetAppIcon from "@material-ui/icons/GetApp";

// Data hooks
import {
  useRegistrationReport,
  useRegistrationWellsDrilldown,
  useRegistrationAdjustments,
} from "../../../../hooks/useRegistrationReport";

// Local hooks
import { useReportSelection, useExportHandlers } from "./hooks";

// Styled components
import { Divider, Breadcrumbs, Card } from "./styled";

// Components
import {
  YearSelector,
  ExportButtons,
  InfoPanel,
  RegistrationTable,
  DrilldownPanel,
  DrilldownMap,
  DrilldownTable,
} from "./components";

/** Label remapping for clarity in the merged table */
const LABEL_REMAP = {
  grand_total: "Total Registered",
  adjusted_total: "Adjusted Total",
};

/** CSV headers for combined report export */
const CSV_HEADERS = [
  "Year/Month",
  "Exempt Grandfathered",
  "Exempt New",
  "Non-Exempt Grandfathered",
  "Class 1",
  "Class 2",
  "Class 3",
  "Monitor Water",
  "Monitor Envr",
  "Total",
];

/**
 * Well Registration Totals report page
 * Displays registration counts by category with drilldown capability
 */
const WellRegistrationTotals = () => {
  // Refs for print/export
  const mainTableRef = useRef();
  const drilldownRef = useRef();

  // Selection state
  const {
    selectedYear,
    selectedRow,
    mapView,
    handleYearChange,
    handleRowClick,
    handleCloseDrilldown,
    handleMapViewChange,
  } = useReportSelection();

  // Data fetching
  const { data, isLoading, error } = useRegistrationReport(selectedYear);

  const { data: adjustmentsData, isLoading: adjustmentsLoading } =
    useRegistrationAdjustments(selectedYear);

  const {
    data: drilldownWells,
    isLoading: drilldownLoading,
    error: drilldownError,
  } = useRegistrationWellsDrilldown(
    selectedRow?.period_start,
    selectedRow?.period_end
  );

  // Combine main data with adjustments (skip 'present' row which duplicates grand total)
  const combinedData = useMemo(() => {
    if (!data) return null;
    const mainRows = data.map((row) =>
      LABEL_REMAP[row.row_kind]
        ? { ...row, row_label: LABEL_REMAP[row.row_kind] }
        : row
    );
    const adjustmentRows = (adjustmentsData || [])
      .filter((row) => row.row_kind !== "present")
      .map((row) => ({
        ...row,
        row_label: LABEL_REMAP[row.row_kind] || row.row_label,
        period_start: null,
        period_end: null,
      }));
    return [...mainRows, ...adjustmentRows];
  }, [data, adjustmentsData]);

  // CSV preparation using combined data
  const prepareCombinedCsvData = useCallback(() => {
    if (!combinedData) return [];
    const rows = combinedData.map((row) => [
      row.row_label,
      row.exempt_grandfathered === null ? "N/A" : row.exempt_grandfathered || 0,
      row.exempt_new || 0,
      row.nonexempt_grandfathered === null
        ? "N/A"
        : row.nonexempt_grandfathered || 0,
      row.class1 || 0,
      row.class2 || 0,
      row.class3 || 0,
      row.monitor_water || 0,
      row.monitor_env || 0,
      row.total || 0,
    ]);
    return [CSV_HEADERS, ...rows];
  }, [combinedData]);

  // Export handlers
  const {
    drilldownTitle,
    handlePrintMain,
    handlePrintDrilldown,
    handlePngExportMain,
    handlePngExportDrilldown,
    prepareCombinedCsvData: prepareCsvDataWithDrilldown,
    drilldownCsvData,
  } = useExportHandlers({
    mainTableRef,
    drilldownRef,
    selectedYear,
    selectedRow,
    drilldownWells,
    prepareCsvData: prepareCombinedCsvData,
  });

  return (
    <>
      <Helmet title="Well Registration Totals" />

      <Typography variant="h3" gutterBottom display="inline">
        Well Registration Totals
      </Typography>

      <Breadcrumbs aria-label="Breadcrumb" mt={2}>
        <Link component={NavLink} exact to="/dashboard">
          Dashboard
        </Link>
        <Typography>Well Registration Totals</Typography>
      </Breadcrumbs>

      <Divider my={6} />

      {/* Info Panel */}
      <InfoPanel />

      {/* Controls */}
      <Card className="no-print">
        <CardContent>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
          >
            <YearSelector value={selectedYear} onChange={handleYearChange} />
            <Button
              variant="contained"
              color="primary"
              size="large"
              startIcon={<GetAppIcon />}
              component={CSVLink}
              data={prepareCsvDataWithDrilldown()}
              filename={`well-registration-totals-${selectedYear}-combined.csv`}
              disabled={!combinedData || combinedData.length === 0}
            >
              Export All CSV
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* Error display */}
      {error && (
        <Card mt={4}>
          <CardContent>
            <Alert severity="error">
              <AlertTitle>Error Loading Report</AlertTitle>
              {error.response?.data?.message ||
                error.message ||
                "An unexpected error occurred while loading the report."}
            </Alert>
          </CardContent>
        </Card>
      )}

      {/* Main tables */}
      <Card mt={4} ref={mainTableRef}>
        <CardContent>
          <Box
            display="flex"
            justifyContent="flex-end"
            mb={2}
            className="no-print"
          >
            <ExportButtons
              csvData={prepareCombinedCsvData()}
              csvFilename={`well-registration-totals-${selectedYear}.csv`}
              onPrint={handlePrintMain}
              onPngExport={handlePngExportMain}
              disabled={!combinedData || combinedData.length === 0}
            />
          </Box>

          <Typography variant="h4" gutterBottom align="center">
            Well Registration Totals - {selectedYear}
          </Typography>

          <RegistrationTable
            data={combinedData}
            isLoading={isLoading || adjustmentsLoading}
            onRowClick={handleRowClick}
            selectedRow={selectedRow}
          />
        </CardContent>
      </Card>

      {/* Hidden drilldown for PDF/PNG export */}
      {selectedRow && drilldownWells && drilldownWells.length > 0 && mapView && (
        <div style={{ position: "absolute", left: "-9999px", width: 900 }}>
          <div ref={drilldownRef} style={{ padding: 16, background: "#fff" }}>
            <Typography variant="h4" gutterBottom align="center">
              {drilldownTitle}
            </Typography>
            <Typography
              variant="subtitle2"
              gutterBottom
              align="center"
              color="textSecondary"
            >
              {drilldownWells.length} well
              {drilldownWells.length !== 1 ? "s" : ""} found
            </Typography>
            <Box mb={2}>
              <DrilldownMap wells={drilldownWells} initialView={mapView} />
            </Box>
            <DrilldownTable wells={drilldownWells} />
          </div>
        </div>
      )}

      {/* Visible drilldown panel */}
      <DrilldownPanel
        selectedRow={selectedRow}
        onClose={handleCloseDrilldown}
        wells={drilldownWells}
        isLoading={drilldownLoading}
        error={drilldownError}
        onPrint={handlePrintDrilldown}
        onPngExport={handlePngExportDrilldown}
        csvData={drilldownCsvData}
        onMapViewChange={handleMapViewChange}
      />
    </>
  );
};

export default WellRegistrationTotals;
